import { __ } from "@wordpress/i18n";
import { useCallback, useEffect, useState } from "@wordpress/element";
import { Spinner } from "@wordpress/components";
import { css } from "@/utils/css";

export type SortOrder = "" | "ASC" | "DESC";

type Column = {
    id: string;
    label: React.ReactNode;
    sortable?: boolean;
};

type Row = {
    id: string;
    actions?: {
        label: string;
        url?: string;
        target?: string;
    }[];
    [key: string]: any;
};

interface Props {
    columns: Column[];
    rows: Row[];
    selectedRows?: string[];
    onRowSelect?: (selectedRows: string[]) => void;
    rowsLoading?: boolean;
    pagination?: {
        total: number;
        currentPage: number;
        limit: number;
        onPageChange?: (page: number) => void;
        onLimitChange?: (limit: number) => void;
    };
    actions?: Array<{
        value: string;
        label: string;
        disabled?: boolean;
        handler?: (selectedItems: string[]) => void | Promise<void>;
    }>;
    onActionSelect?: (action: string) => void;
    selectedAction?: string;
    search?: string;
    onSearch?: (search: string) => void;
    sorting?: {
        orderby?: string;
        order?: SortOrder;
    };
    onSort?: (orderby: string, order: SortOrder) => void;
    filter?: {
        options: { value: string; label: string }[];
        value: string;
        label?: string;
        onChange: (value: string) => void;
    };
}

export function Table({
    columns,
    rows,
    selectedRows,
    onRowSelect,
    actions,
    pagination,
    selectedAction,
    onActionSelect,
    rowsLoading,
    search,
    onSearch,
    sorting,
    onSort,
    filter,
}: Props) {
    actions = Array.isArray(actions) ? actions : [];
    pagination = pagination instanceof Object ? pagination : { total: rows.length, currentPage: 1, limit: rows.length };

    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [actionSubmitting, setActionSubmitting] = useState(false);

    useEffect(() => {
        if (Array.isArray(selectedRows)) {
            setSelectedItems(selectedRows);
        }
    }, [selectedRows]);

    const handleItemsSelect = (items: string[]) => {
        setSelectedItems(items);
        onRowSelect?.(items);
    };

    const onActionSubmit = async () => {
        const targetedAction = actions.find((action) => action.value === selectedAction);
        setActionSubmitting(true);
        try {
            await targetedAction?.handler?.(selectedItems);
        } finally {
            setActionSubmitting(false);
        }
    };

    const tableNavProps = {
        selectedAction,
        onActionSelect,
        onActionSubmit,
        actionSubmitting,
        actions,
        search,
        onSearch,
        sorting,
        filter,
        ...pagination,
    };

    const columnsProps = {
        columns,
        sorting,
        onSort,
        onSelectAll: (checked: boolean) => {
            if (checked) {
                handleItemsSelect(rows.map((row) => row.id));
            } else {
                handleItemsSelect([]);
            }
        },
    };

    const NoItemRow = useCallback(
        ({ children }: React.PropsWithChildren) => (
            <tr className="no-items">
                <td className="colspanchange" colSpan={columns.length + 1}>
                    {children}
                </td>
            </tr>
        ),
        [columns]
    );

    return (
        <>
            <TableNav position="top" {...tableNavProps} />
            <table className="wp-list-table widefat fixed striped table-view-excerpt toplevel_page_antimanual">
                <thead>
                    <Columns {...columnsProps} />
                </thead>

                <tbody id="the-list">
                    {rowsLoading ? (
                        <NoItemRow>
                            <div className={css("p-16 flex justify-center")}>
                                <Spinner />
                            </div>
                        </NoItemRow>
                    ) : !rows.length ? (
                        <NoItemRow>{__("No items found", "antimanual")}</NoItemRow>
                    ) : (
                        rows.map((row) => {
                            const hasActions = !!row.actions?.length;
                            const items = selectedRows || selectedItems;
                            const isSelected = items.includes(row.id);

                            return (
                                <tr key={row.id} style={{ backgroundColor: isSelected ? "#dbeafe" : "" }}>
                                    <th scope="row" className="check-column">
                                        <input
                                            type="checkbox"
                                            name="selected_items[]"
                                            value={row.id}
                                            checked={isSelected}
                                            onChange={() => {
                                                if (isSelected) {
                                                    handleItemsSelect(items.filter((id) => id !== row.id));
                                                } else {
                                                    handleItemsSelect([...items, row.id]);
                                                }
                                            }}
                                        />
                                    </th>
                                    {columns.map((column, i) => {
                                        const isPrimary = i === 0;

                                        return (
                                            <td
                                                className={`${column.id} column-${column.id} ${isPrimary ? "column-primary" : ""} ${
                                                    hasActions ? "has-row-actions" : ""
                                                }`}
                                            >
                                                {row[column.id]}
                                                {hasActions && isPrimary ? (
                                                    <div className="row-actions">
                                                        {row.actions!.map((action, index) => {
                                                            return (
                                                                <span>
                                                                    <a href={action.url} target={action.target}>
                                                                        {action.label}
                                                                    </a>
                                                                    {index === row.actions!.length - 1 ? "" : " | "}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                ) : null}

                                                <button type="button" className="toggle-row">
                                                    <span className="screen-reader-text">{__("Show more details", "antimanual")}</span>
                                                </button>
                                                <button type="button" className="toggle-row">
                                                    <span className="screen-reader-text">{__("Show more details", "antimanual")}</span>
                                                </button>
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })
                    )}
                </tbody>

                <tfoot>
                    <Columns {...columnsProps} />
                </tfoot>
            </table>
            <TableNav position="bottom" {...tableNavProps} />
        </>
    );
}

type ColumnsProps = {
    columns: Column[];
    onSelectAll: (checked: boolean) => void;
    sorting?: {
        orderby?: string;
        order?: SortOrder;
    };
    onSort?: (orderby: string, order: SortOrder) => void;
};

function Columns({ columns, onSelectAll, sorting, onSort }: ColumnsProps) {
    const handleSort = (columnId: string) => {
        if (!onSort) return;

        const currentOrderby = sorting?.orderby;
        const currentOrder = sorting?.order;

        if (currentOrderby !== columnId) {
            onSort(columnId, 'ASC');
        }

        else if (currentOrder === 'ASC') {
            onSort(columnId, 'DESC');
        } else if (currentOrder === 'DESC') {
            onSort('', '');
        } else {
            onSort(columnId, 'ASC');
        }
    };

    return (
        <tr>
            <td id="cb" className="manage-column column-cb check-column">
                <input id="cb-select-all-2" type="checkbox" onChange={(e) => onSelectAll(e.target.checked)} />
                <label htmlFor="cb-select-all-2">
                    <span className="screen-reader-text">{__("Select All", "antimanual")}</span>
                </label>
            </td>
            {columns.map((column, i) => {
                const isPrimary = i === 0;
                const isCurrentSort = sorting?.orderby === column.id;
                const sortOrder = isCurrentSort ? sorting?.order?.toLowerCase() : '';
                const sortClass = column.sortable && isCurrentSort ? `sorted ${sortOrder}` : (column.sortable ? 'sortable desc' : '');

                return (
                    <th
                        scope="col"
                        id={column.id}
                        className={`manage-column column-${column.id} ${isPrimary ? "column-primary" : ""} ${sortClass}`}
                    >
                        {column.sortable ? (
                            <a
                                href=""
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleSort(column.id);
                                }}
                            >
                                <span>{column.label}</span>
                                <span className="sorting-indicators">
                                    <span className="sorting-indicator asc" aria-hidden></span>
                                    <span className="sorting-indicator desc" aria-hidden></span>
                                </span>
                            </a>
                        ) : (
                            column.label
                        )}
                    </th>
                );
            })}
        </tr>
    );
}

type TableNavProps = {
    total: number;
    currentPage: number;
    onPageChange?: (page: number) => void;
    limit: number;
    position: "top" | "bottom";
    actions: Array<{
        disabled?: boolean;
        value: string;
        label: string;
    }>;
    selectedAction?: string;
    onActionSelect?: (action: string) => void;
    onActionSubmit?: () => void;
    actionSubmitting?: boolean;
    search?: string;
    onSearch?: (search: string) => void;
    onLimitChange?: (limit: number) => void;
    filter?: {
        options: { value: string; label: string }[];
        value: string;
        label?: string;
        onChange: (value: string) => void;
    };
};

function TableNav({
    total,
    currentPage,
    limit,
    position = "top",
    actions,
    onPageChange,
    selectedAction,
    onActionSelect,
    onActionSubmit,
    actionSubmitting,
    search,
    onSearch,
    onLimitChange,
    filter,
}: TableNavProps) {
    const canPaginate = total > limit;
    const totalPages = Math.ceil(total / limit);
    const isFirstPage = currentPage <= 1;
    const isLastPage = total <= currentPage * limit;

    const onNextPage = () => {
        if (!isLastPage) {
            onPageChange?.(currentPage + 1);
        }
    };
    const onPrevPage = () => {
        if (!isFirstPage) {
            onPageChange?.(currentPage - 1);
        }
    };
    const onFirstPage = () => {
        if (!isFirstPage) {
            onPageChange?.(1);
        }
    };
    const onLastPage = () => {
        if (!isLastPage) {
            onPageChange?.(totalPages);
        }
    };

    return (
        <div className={`tablenav ${position}`}>
            {actions.length ? (
                <div className="alignleft actions bulkactions">
                    <label htmlFor={`bulk-action-selector-${position}`} className="screen-reader-text">
                        {__("Select bulk action", "antimanual")}
                    </label>
                    <select
                        name="action"
                        id={`bulk-action-selector-${position}`}
                        onChange={(e) => onActionSelect?.(e.target.value)}
                        value={selectedAction || "-1"}
                    >
                        <option value="-1" disabled>
                            {__("Bulk actions", "antimanual")}
                        </option>
                        {actions.map((action) => (
                            <option key={action.value} value={action.value} disabled={action.disabled}>
                                {action.label}
                            </option>
                        ))}
                    </select>
                    <input
                        onClick={onActionSubmit}
                        type="button"
                        name="bulk_action"
                        id={position === "top" ? "doaction" : "doaction2"}
                        className="button action"
                        value={__("Apply", "antimanual")}
                        disabled={actionSubmitting}
                    />
                </div>
            ) : null}
            {position === "top" && onLimitChange && (
                <div className="alignleft actions">
                    <select
                        onChange={(e) => onLimitChange(Number(e.target.value))}
                        value={limit}
                        aria-label={__("Rows per page", "antimanual")}
                    >
                        {[10, 20, 50, 100, 1000].map(val => (
                            <option key={val} value={val}>
                                {val} {__("Items", "antimanual")}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            {position === "top" && search !== undefined && (
                <div className="alignleft" style={{ display: 'flex', alignItems: 'center' }}>
                    <input 
                        type="search" 
                        name="search" 
                        value={search}
                        onChange={(e) => onSearch?.(e.target.value)}
                        placeholder={__("Search...", "antimanual")}
                        style={{ marginRight: 6 }} 
                    />
                </div>
            )}
            {position === "top" && filter && filter.options.length > 0 && (
                <div className="alignleft actions">
                    {filter.label && (
                        <label htmlFor="table-filter" className="screen-reader-text">
                            {filter.label}
                        </label>
                    )}
                    <select
                        id="table-filter"
                        value={filter.value}
                        onChange={(e) => filter.onChange(e.target.value)}
                        aria-label={filter.label || __("Filter", "antimanual")}
                    >
                        {filter.options.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            <div className="tablenav-pages">
                <span className="displaying-num">
                    {total} {total === 1 ? __("item", "antimanual") : __("items", "antimanual")}
                </span>
                {canPaginate ? (
                    <span className="pagination-links">
                        <span
                            className={`tablenav-pages-navspan button ${isFirstPage ? "disabled" : ""}`}
                            aria-hidden
                            onClick={(e) => {
                                e.preventDefault();
                                onFirstPage?.();
                            }}
                        >
                            «
                        </span>{" "}
                        <span
                            className={`tablenav-pages-navspan button ${isFirstPage ? "disabled" : ""}`}
                            aria-hidden
                            onClick={(e) => {
                                e.preventDefault();
                                onPrevPage?.();
                            }}
                        >
                            ‹
                        </span>{" "}
                        {position === "top" ? (
                            <span className="paging-input">
                                <label htmlFor="current-page-selector" className="screen-reader-text">
                                    {__("Current Page", "antimanual")}
                                </label>
                                <input
                                    className="current-page"
                                    id="current-page-selector"
                                    type="text"
                                    name="paged"
                                    value={currentPage}
                                    size={1}
                                    aria-describedby="table-paging"
                                    onChange={(e) => {
                                        const targetPage = parseInt(e.target.value, 10);
                                        if (targetPage > 0 && targetPage <= totalPages) onPageChange?.(targetPage);
                                    }}
                                />
                                <span className="tablenav-paging-text">
                                    {" "}
                                    {__("of", "antimanual")} <span className="total-pages">{totalPages}</span>
                                </span>
                            </span>
                        ) : (
                            <>
                                <span className="screen-reader-text">{__("Current Page", "antimanual")}</span>
                                <span id="table-paging" className="paging-input">
                                    <span className="tablenav-paging-text">
                                        {currentPage} {__("of", "antimanual")} <span className="total-pages">{totalPages}</span>
                                    </span>
                                </span>
                            </>
                        )}{" "}
                        <a
                            className={`next-page button ${isLastPage ? "disabled" : ""}`}
                            href=""
                            onClick={(e) => {
                                e.preventDefault();
                                onNextPage?.();
                            }}
                        >
                            <span className="screen-reader-text">{__("Next page", "antimanual")}</span>
                            <span aria-hidden>›</span>
                        </a>{" "}
                        <a
                            className={`last-page button ${isLastPage ? "disabled" : ""}`}
                            href=""
                            onClick={(e) => {
                                e.preventDefault();
                                onLastPage?.();
                            }}
                        >
                            <span className="screen-reader-text">{__("Last page", "antimanual")}</span>
                            <span aria-hidden>»</span>
                        </a>
                    </span>
                ) : null}
            </div>
            <br className="clear" />
        </div>
    );
}
